import { useState } from "react";
import { format } from "date-fns";
import { openExternal } from "@/lib/utils";
import {
  Building2,
  ExternalLink,
  User,
  Mail,
  Linkedin,
  Package,
  Pencil,
  Trash2,
  X,
  FileText,
  FolderSearch,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PipelineOpportunity } from "@/types/pipeline";
import { REVENUE_REGULARITIES } from "@/types/pipeline";
import { VoiceNoteInput } from "@/components/pipeline/VoiceNoteInput";
import {
  PipelineStage,
  PipelineProduct,
  PipelineActivity,
  useUpdateOpportunity,
  useArchiveOpportunity,
} from "@/hooks/usePipeline";
import {
  useOpportunityActivities,
  useOpportunityContacts,
} from "@/hooks/useOpportunityDetail";
import { StrategyOverviewTab } from "@/components/pipeline/tabs/StrategyOverviewTab";
import { EngagementTimelineTab } from "@/components/pipeline/tabs/EngagementTimelineTab";
import { OpportunityContactsTab } from "@/components/pipeline/tabs/OpportunityContactsTab";
import { MeetingPrepTab } from "@/components/pipeline/tabs/MeetingPrepTab";
import { useMeetingPreps } from "@/hooks/useMeetingPrep";
import { toast } from "sonner";

interface OpportunityModalProps {
  opportunity: PipelineOpportunity | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  stages: PipelineStage[];
  products: PipelineProduct[];
  personas: { id: string; full_name: string; email: string | null; title: string | null }[];
  onRefetch: () => void;
}

function formatCurrency(value: number | null) {
  if (!value) return "$0";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
}

function StageBadge({ stageName, stageColor, probability }: { stageName: string; stageColor: string; probability: number | null }) {
  return (
    <Badge
      style={{ backgroundColor: stageColor, color: "#fff" }}
      className="text-xs"
    >
      {stageName} {probability != null && `(${probability}%)`}
    </Badge>
  );
}

export function OpportunityModal({
  opportunity,
  open,
  onOpenChange,
  stages,
  products,
  personas,
  onRefetch,
}: OpportunityModalProps) {
  const updateOpportunity = useUpdateOpportunity();
  const archiveOpportunity = useArchiveOpportunity();

  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState<Record<string, string> | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Fetch detail data
  const { data: activities = [] } = useOpportunityActivities(opportunity?.id);
  const { data: contacts = [] } = useOpportunityContacts(opportunity?.id);
  const { data: meetingPreps = [] } = useMeetingPreps(opportunity?.id);

  const startEditing = () => {
    if (!opportunity) return;
    setEditFormData({
      name: opportunity.name || "",
      stage_id: opportunity.stage_id || "",
      product_id: opportunity.product_id || "",
      deal_value: opportunity.deal_value?.toString() || "",
      owner_id: opportunity.owner_id || "",
      expected_close_date: opportunity.expected_close_date || "",
      next_action: opportunity.next_action || "",
      next_action_date: opportunity.next_action_date || "",
      notes: opportunity.notes || "",
      company_domain: opportunity.company_domain || "",
      main_contact_name: opportunity.main_contact_name || "",
      main_contact_email: opportunity.main_contact_email || "",
      main_contact_linkedin: opportunity.main_contact_linkedin || "",
      revenue_regularity: opportunity.revenue_regularity || "",
      opp_summary: opportunity.opp_summary || "",
      single_sales_objective: opportunity.single_sales_objective || "",
    });
    setIsEditing(true);
  };

  const handleSave = () => {
    if (!opportunity || !editFormData) return;
    updateOpportunity.mutate(
      {
        id: opportunity.id,
        name: editFormData.name,
        stage_id: editFormData.stage_id || undefined,
        product_id: editFormData.product_id || null,
        deal_value: editFormData.deal_value ? parseFloat(editFormData.deal_value) : null,
        owner_id: editFormData.owner_id || null,
        expected_close_date: editFormData.expected_close_date || null,
        next_action: editFormData.next_action || null,
        next_action_date: editFormData.next_action_date || null,
        notes: editFormData.notes || null,
        company_domain: editFormData.company_domain || null,
        main_contact_name: editFormData.main_contact_name || null,
        main_contact_email: editFormData.main_contact_email || null,
        main_contact_linkedin: editFormData.main_contact_linkedin || null,
        revenue_regularity: editFormData.revenue_regularity || null,
        opp_summary: editFormData.opp_summary || null,
        single_sales_objective: editFormData.single_sales_objective || null,
      },
      {
        onSuccess: () => {
          toast.success("Opportunity updated");
          setIsEditing(false);
          setEditFormData(null);
          onRefetch();
        },
        onError: () => toast.error("Failed to update opportunity"),
      }
    );
  };

  const handleArchive = () => {
    if (!opportunity) return;
    archiveOpportunity.mutate(opportunity.id, {
      onSuccess: () => {
        toast.success("Opportunity archived");
        onOpenChange(false);
        setShowDeleteConfirm(false);
        onRefetch();
      },
      onError: () => toast.error("Failed to archive opportunity"),
    });
  };

  if (!opportunity) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => { if (!v) { setIsEditing(false); setEditFormData(null); } onOpenChange(v); }}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto p-0">
          {/* Header */}
          <div className="sticky top-0 z-10 bg-background border-b px-6 py-4">
            <DialogHeader className="flex-row items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                {isEditing && editFormData ? (
                  <Input
                    value={editFormData.name}
                    onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                    className="text-lg font-semibold h-auto py-1"
                  />
                ) : (
                  <DialogTitle className="text-lg font-semibold truncate pr-4">
                    {opportunity.name}
                  </DialogTitle>
                )}

                {/* Quick badges row */}
                {isEditing && editFormData ? (
                  <div className="grid grid-cols-3 gap-2 mt-3">
                    <Select
                      value={editFormData.stage_id}
                      onValueChange={(v) => setEditFormData({ ...editFormData, stage_id: v })}
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue placeholder="Stage" />
                      </SelectTrigger>
                      <SelectContent>
                        {stages.map((s) => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.name} ({s.probability}%)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={editFormData.product_id}
                      onValueChange={(v) => setEditFormData({ ...editFormData, product_id: v })}
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue placeholder="Product" />
                      </SelectTrigger>
                      <SelectContent>
                        {products.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={editFormData.owner_id}
                      onValueChange={(v) => setEditFormData({ ...editFormData, owner_id: v })}
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue placeholder="Rep / Owner" />
                      </SelectTrigger>
                      <SelectContent>
                        {personas.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.full_name}{p.title ? ` — ${p.title}` : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2 mt-2">
                    <StageBadge
                      stageName={opportunity.stage_name}
                      stageColor={opportunity.stage_color}
                      probability={opportunity.stage_probability || opportunity.probability}
                    />
                    {opportunity.owner_name ? (
                      <Badge variant="secondary" className="bg-primary/80 text-primary-foreground text-xs">
                        {opportunity.owner_name}
                      </Badge>
                    ) : (
                      <Badge variant="destructive" className="opacity-70 text-xs">No rep</Badge>
                    )}
                    {opportunity.product_name ? (
                      <Badge variant="outline" className="text-xs">
                        <Package className="h-3 w-3 mr-1" />
                        {opportunity.product_name}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-muted-foreground text-xs">
                        <Package className="h-3 w-3 mr-1" />No product
                      </Badge>
                    )}
                    {opportunity.deal_value && (
                      <Badge variant="outline" className="text-xs font-semibold">
                        {formatCurrency(opportunity.deal_value)}
                      </Badge>
                    )}
                    {opportunity.contact_classification && (
                      <Badge variant="outline" className="text-xs">{opportunity.contact_classification}</Badge>
                    )}
                  </div>
                )}
              </div>

              {/* Action buttons */}
              <div className="flex gap-2 shrink-0 pt-1">
                {isEditing ? (
                  <>
                    <Button variant="outline" size="sm" onClick={() => { setIsEditing(false); setEditFormData(null); }}>
                      <X className="h-4 w-4 mr-1" />Cancel
                    </Button>
                    <Button size="sm" onClick={handleSave} disabled={updateOpportunity.isPending}>
                      {updateOpportunity.isPending ? "Saving..." : "Save"}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="outline" size="sm" onClick={startEditing}>
                      <Pencil className="h-4 w-4 mr-1" />Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => setShowDeleteConfirm(true)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>
            </DialogHeader>

            {/* Company & contact quick-info bar */}
            {!isEditing && (
              <div className="flex items-center gap-4 mt-3 pt-3 border-t flex-wrap text-sm">
                {opportunity.account_id ? (
                  <a
                    href={`/accounts/${opportunity.account_id}`}
                    className="flex items-center gap-1.5 text-primary hover:underline font-medium"
                  >
                    <Building2 className="h-3.5 w-3.5" />
                    {opportunity.account_name || opportunity.name}
                  </a>
                ) : opportunity.company_domain ? (
                  <a
                    href={`https://${opportunity.company_domain}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-primary hover:underline"
                  >
                    <Building2 className="h-3.5 w-3.5" />
                    {opportunity.company_domain}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                ) : null}
                {opportunity.main_contact_name && (
                  <span className="flex items-center gap-1.5 text-muted-foreground">
                    <User className="h-3.5 w-3.5" />
                    {opportunity.main_contact_name}
                  </span>
                )}
                {opportunity.main_contact_email && (
                  <a href={`mailto:${opportunity.main_contact_email}`} className="flex items-center gap-1.5 text-primary hover:underline">
                    <Mail className="h-3.5 w-3.5" />
                    {opportunity.main_contact_email}
                  </a>
                )}
                {opportunity.main_contact_linkedin && (
                  <button
                    type="button"
                    onClick={() => openExternal(opportunity.main_contact_linkedin)}
                    className="flex items-center gap-1.5 text-primary hover:underline"
                  >
                    <Linkedin className="h-3.5 w-3.5" />
                    LinkedIn
                  </button>
                )}
                {opportunity.account_id && (
                  <a
                    href={`/accounts/${opportunity.account_id}`}
                    className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary ml-auto"
                  >
                    <FileText className="h-3 w-3" />
                    Briefing Sheet
                  </a>
                )}
              </div>
            )}

            {/* Edit-mode: company & contact fields */}
            {isEditing && editFormData && (
              <div className="space-y-3 mt-3 pt-3 border-t">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Company Domain</Label>
                    <Input
                      value={editFormData.company_domain}
                      onChange={(e) => setEditFormData({ ...editFormData, company_domain: e.target.value })}
                      placeholder="acmecorp.com"
                      className="h-8 text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Contact Name</Label>
                    <Input
                      value={editFormData.main_contact_name}
                      onChange={(e) => setEditFormData({ ...editFormData, main_contact_name: e.target.value })}
                      placeholder="John Smith"
                      className="h-8 text-sm"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Contact Email</Label>
                    <Input
                      type="email"
                      value={editFormData.main_contact_email}
                      onChange={(e) => setEditFormData({ ...editFormData, main_contact_email: e.target.value })}
                      placeholder="john@acme.com"
                      className="h-8 text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Contact LinkedIn</Label>
                    <Input
                      value={editFormData.main_contact_linkedin}
                      onChange={(e) => setEditFormData({ ...editFormData, main_contact_linkedin: e.target.value })}
                      placeholder="linkedin.com/in/..."
                      className="h-8 text-sm"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Deal Value</Label>
                    <Input
                      type="number"
                      value={editFormData.deal_value}
                      onChange={(e) => setEditFormData({ ...editFormData, deal_value: e.target.value })}
                      placeholder="10000"
                      className="h-8 text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Revenue Type</Label>
                    <Select
                      value={editFormData.revenue_regularity}
                      onValueChange={(v) => setEditFormData({ ...editFormData, revenue_regularity: v })}
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {REVENUE_REGULARITIES.map((r) => (
                          <SelectItem key={r.value} value={r.value}>
                            {r.icon} {r.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Expected Close</Label>
                    <Input
                      type="date"
                      value={editFormData.expected_close_date}
                      onChange={(e) => setEditFormData({ ...editFormData, expected_close_date: e.target.value })}
                      className="h-8 text-sm"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Next Action</Label>
                    <Input
                      value={editFormData.next_action}
                      onChange={(e) => setEditFormData({ ...editFormData, next_action: e.target.value })}
                      placeholder="Schedule discovery call"
                      className="h-8 text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Next Action Date</Label>
                    <Input
                      type="date"
                      value={editFormData.next_action_date}
                      onChange={(e) => setEditFormData({ ...editFormData, next_action_date: e.target.value })}
                      className="h-8 text-sm"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Single Sales Objective</Label>
                  <Textarea
                    value={editFormData.single_sales_objective}
                    onChange={(e) => setEditFormData({ ...editFormData, single_sales_objective: e.target.value })}
                    placeholder="The specific, measurable outcome this opportunity aims to achieve..."
                    rows={2}
                    className="text-sm mt-1"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-xs">Summary</Label>
                    <VoiceNoteInput
                      contextId={`summary-${opportunity.id}`}
                      onTranscript={(text) =>
                        setEditFormData((prev) => ({
                          ...prev,
                          opp_summary: prev.opp_summary
                            ? `${prev.opp_summary}\n\n${text}`
                            : text,
                        }))
                      }
                    />
                  </div>
                  <Textarea
                    value={editFormData.opp_summary}
                    onChange={(e) => setEditFormData({ ...editFormData, opp_summary: e.target.value })}
                    placeholder="Brief summary..."
                    rows={2}
                    className="text-sm"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-xs">Notes</Label>
                    <VoiceNoteInput
                      contextId={`notes-${opportunity.id}`}
                      onTranscript={(text) =>
                        setEditFormData((prev) => ({
                          ...prev,
                          notes: prev.notes
                            ? `${prev.notes}\n\n${text}`
                            : text,
                        }))
                      }
                    />
                  </div>
                  <Textarea
                    value={editFormData.notes}
                    onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                    placeholder="Additional notes..."
                    rows={2}
                    className="text-sm"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Tabbed Content */}
          {!isEditing && (
            <div className="px-6 pb-6 pt-4">
              <Tabs defaultValue="strategy" className="w-full">
                <TabsList className="grid w-full grid-cols-5 mb-4">
                  <TabsTrigger value="strategy">Strategy</TabsTrigger>
                  <TabsTrigger value="timeline">
                    Timeline {activities.length > 0 && `(${activities.length})`}
                  </TabsTrigger>
                  <TabsTrigger value="contacts">
                    Contacts {contacts.length > 0 && `(${contacts.length})`}
                  </TabsTrigger>
                  <TabsTrigger value="meeting-prep">
                    Meeting Prep {meetingPreps.length > 0 && `(${meetingPreps.length})`}
                  </TabsTrigger>
                  <TabsTrigger value="research">Research</TabsTrigger>
                </TabsList>

                <TabsContent value="strategy">
                  <StrategyOverviewTab opportunity={opportunity} />
                </TabsContent>

                <TabsContent value="timeline">
                  <EngagementTimelineTab
                    opportunityId={opportunity.id}
                    activities={activities}
                  />
                </TabsContent>

                <TabsContent value="contacts">
                  <OpportunityContactsTab
                    opportunityId={opportunity.id}
                    contacts={contacts}
                    mainContactName={opportunity.main_contact_name}
                    mainContactEmail={opportunity.main_contact_email}
                    accountId={opportunity.account_id}
                  />
                </TabsContent>

                <TabsContent value="meeting-prep">
                  <MeetingPrepTab
                    opportunityId={opportunity.id}
                    accountId={opportunity.account_id}
                    accountName={opportunity.account_name}
                    opportunityName={opportunity.name}
                    opportunityContacts={contacts}
                    stageName={opportunity.stage_name}
                    stageDescription={opportunity.stage_description}
                  />
                </TabsContent>

                <TabsContent value="research">
                  <div className="text-center py-16">
                    <FolderSearch className="h-10 w-10 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-medium mb-1">Research & Documents</h3>
                    <p className="text-sm text-muted-foreground max-w-md mx-auto">
                      Research documents, meeting transcripts, and proposal files for this
                      opportunity will appear here once connected.
                    </p>
                    {opportunity.account_id && (
                      <Button variant="outline" className="mt-4" asChild>
                        <a href={`/accounts/${opportunity.account_id}`}>
                          <FileText className="h-4 w-4 mr-2" />
                          View Account Briefing Sheet
                        </a>
                      </Button>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Archive Confirmation */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive Opportunity</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to archive "{opportunity.name}"?
              This will remove it from the pipeline view but keep the record.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleArchive}
            >
              {archiveOpportunity.isPending ? "Archiving..." : "Archive"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
